<template>
  <Screen
    v-if="isUserCreatorInGroup || isProjectVisibleToSales"
    back-button
    back-button-text="Projects"
    back-button-route="/projects"
    :header-title="headerTitle"
    center
    left
    left-bigger
    left-auto-height
    :user="user"
    :group-id="selectedGroupId"
  >
    <aside
      slot="screen-left"
      class="project__left"
    >
      <ElCard class="project__left-actions">
        <div
          v-if="isUserCreatorInGroup"
          class="project__left-action"
        >
          <router-link :to="`/projects/${projectId}/create`">
            <ElLink
              icon="el-icon-edit"
              :underline="false"
            >
              Edit Boards
            </ElLink>
          </router-link>
        </div>

        <div class="project__left-action">
          <ElLink
            icon="el-icon-data-board"
            :underline="false"
            @click="showPresent"
          >
            {{ isUserCreatorInGroup ? 'Present' : 'Boards' }}
          </ElLink>
        </div>

        <div class="project__left-action">
          <router-link
            :to="
              `/collection?group_id=${selectedGroupId}&season_id=${selectedSeasonId}`
            "
          >
            <ElLink
              icon="el-icon-s-grid"
              :underline="false"
            >
              Collection Overview
            </ElLink>
          </router-link>
        </div>

        <div
          v-if="isUserCreatorInGroup"
          class="project__left-action"
        >
          <router-link :to="`/projects/${projectId}/feedback`">
            <ElLink
              icon="el-icon-d-caret"
              :underline="false"
            >
              See Ratings
            </ElLink>
          </router-link>
        </div>

        <div
          v-if="isUserCreatorInGroup"
          class="project__left-action"
        >
          <ElLink
            icon="el-icon-search"
            :underline="false"
            @click="updateProjectVisibleToSales"
          >
            Visible
          </ElLink>
          <ElSwitch
            :value="isProjectVisibleToSales"
            @change="updateProjectVisibleToSales"
          />
        </div>

        <div
          v-if="canUserAccessFeaturePDFExport"
          class="project__left-action"
        >
          <ElDropdown
            class="pdf-export"
            placement="right-start"
            @command="printPDFCommand"
          >
            <ElLink
              icon="el-icon-download"
              :underline="false"
            >
              Export PDF
            </ElLink>
            <ElDropdownMenu slot="dropdown">
              <ElDropdownItem
                :command="{ itemTypeToExport: validPDFExportTypes.BOARDS }"
              >
                Boards
              </ElDropdownItem>
              <ElDropdownItem
                :command="{ itemTypeToExport: validPDFExportTypes.PRESENT }"
              >
                Detailed Styles
              </ElDropdownItem>
              <ElDropdownItem
                :command="{ itemTypeToExport: validPDFExportTypes.COLLECTION }"
              >
                Overview of all Styles
              </ElDropdownItem>
            </ElDropdownMenu>
          </ElDropdown>
        </div>

        <div
          v-if="canUserAccessFeaturePDFQualitySelection"
          class="project__left-action"
        >
          <ElDropdown
            class="pdf-export"
            placement="right-start"
            @command="printPDFCommand"
          >
            <ElLink
              icon="el-icon-download"
              :underline="false"
            >
              Export Boards PDF
            </ElLink>
            <ElDropdownMenu slot="dropdown">
              <ElDropdownItem
                :command="{ itemTypeToExport: validPDFExportTypes.BOARDS, quality: 1 }"
              >
                Average quality / fast
              </ElDropdownItem>
              <ElDropdownItem
                :command="{ itemTypeToExport: validPDFExportTypes.BOARDS, quality: 2 }"
              >
                Good quality / standard
              </ElDropdownItem>
              <ElDropdownItem
                :command="{ itemTypeToExport: validPDFExportTypes.BOARDS, quality: 4 }"
              >
                Excellent quality / slow
              </ElDropdownItem>
            </ElDropdownMenu>
          </ElDropdown>
        </div>

        <div
          v-else
          class="project__left-action"
          @click="printPDFCommand({ itemTypeToExport: validPDFExportTypes.BOARDS })"
        >
          <ElLink
            icon="el-icon-download"
            :underline="false"
          >
            Export Boards PDF
          </ElLink>
        </div>

        <div v-if="isUserCreatorInGroup">
          <br>
          <br>
          <div class="project__left-action">
            <ElLink
              icon="el-icon-close"
              type="danger"
              :underline="false"
              @click="showConfirmProjectDeleteModal"
            >
              Delete Project
            </ElLink>

            <StitchDialog
              v-if="project"
              :visible.sync="showConfirmProjectDelete"
              :show-close="false"
            >
              <div slot="title">
                Are you 100% Sure?
              </div>
              <div>
                <span class="project__delete-name">{{
                  projectToDelete.projectName
                }}</span>
                will be gone for good.
              </div>
              <div slot="footer">
                <ElButton
                  @click="showConfirmProjectDelete = false"
                >
                  No
                </ElButton>
                <ElButton
                  type="danger"
                  @click="confirmProjectDelete"
                >
                  Yes
                </ElButton>
              </div>
            </StitchDialog>
          </div>
        </div>
      </ElCard>
    </aside>

    <section
      slot="screen-center"
      class="project"
    >
      <section
        v-loading="isLoading"
        element-loading-text="Loading all boards..."
      >
        <div
          v-if="hasBoards"
          class="project__boards"
        >
          <div
            v-for="(board, boardsIndex) in boards"
            :key="boardsIndex"
            v-observe-visibility="
              (isVisible, entry) =>
                fetchBoardDetailedIfNeeded(isVisible, board.id)
            "
            :class="[
              'project__board',
              { 'project__board--click-disabled': !isUserCreatorInGroup }
            ]"
            @click="isUserCreatorInGroup ? showProjectCreate(board.id) : null"
          >
            <BoardPreview
              all-boards-mode
              :template-id="board.layout"
              :board-id="board.id"
              :image-resolution="imageResolution(board.layout)"
            />
          </div>
        </div>

        <div
          v-else
          class="project__empty"
        >
          No boards available yet...
        </div>
      </section>
    </section>
  </Screen>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

import { imageResolutions } from '@/constants/image'

import Screen from '@/components/Screen.vue'
import BoardPreview from '@/components/project/BoardPreview.vue'
import { Workspace } from '@/mixins/workspace'
import { Projects } from '@/mixins/projects'

import Utils from '@/services/utils'

import features from '@/constants/features.js'

export default {
  name: 'AllBoards',

  components: {
    Screen,
    BoardPreview
  },

  mixins: [Workspace, Projects],

  data: () => ({
    showConfirmProjectDelete: false,
    isProjectVisibleToSales: false,
    projectToDelete: {},
    isLoading: true,
    boardsLoaded: {},
    isLoadingBoards: {},
    validPDFExportTypes: {
      BOARDS: 'boards',
      COLLECTION: 'collection',
      PRESENT: 'present'
    }
  }),

  computed: {
    ...mapGetters(['getBoardById']),

    /**
     * @returns {boolean}
     */
    isUserCreatorInGroup () {
      return Utils.isUserCreatorInGroup(this.user, this.selectedGroupId)
    },

    /**
     * @returns {boolean}
     */
    canUserAccessFeaturePDFExport () {
      if (!this.user) {
        return false
      }

      return this.$featureFlagsManager.canAccessFeature(features.PDF_EXPORT, {
        user: this.user
      })
    },

    /**
     * @returns {boolean}
     */
    canUserAccessFeaturePDFQualitySelection () {
      if (!this.user) {
        return false
      }

      return this.$featureFlagsManager.canAccessFeature(features.PDF_QUALITY_SELECTION, {
        user: this.user
      })
    },

    /**
     * @returns {number}
     */
    defaultPdfQuality () {
      return this.$remoteSettings.defaultPdfQuality
    }
  },

  async created () {
    this.isLoading = true

    await this.fetchProject({
      projectId: this.projectId
    })

    this.updateSelectedGroupIdAndSelectedSeasonId(this.project)

    if (this.project) {
      this.isProjectVisibleToSales = this.project.visible_to_sales
    }

    this.isLoading = false
  },

  methods: {
    ...mapActions([
      'fetchProject',
      'fetchBoard',
      'deleteProject',
      'updateProject'
    ]),

    /**
     * @param {string} boardId
     */
    showProjectCreate (boardId) {
      localStorage.setItem(`activeBoard-${this.projectId}`, boardId)
      this.$router.push({ path: `/projects/${this.projectId}/create` })
    },

    /**
     * @param {boolean} isVisible
     * @param {number}  boardId
     */
    async fetchBoardDetailedIfNeeded (isVisible, boardId) {
      if (isVisible) {
        if (!this.boardsLoaded[boardId] && !this.isLoadingBoards[boardId]) {
          this.$set(this.isLoadingBoards, boardId, true)

          await this.fetchBoard({
            projectId: this.projectId,
            boardId: boardId
          })

          this.$set(this.boardsLoaded, boardId, true)
          this.$delete(this.isLoadingBoards, boardId)
        }
      }
    },

    /**
     */
    showConfirmProjectDeleteModal () {
      this.showConfirmProjectDelete = true
      this.projectToDelete = {
        projectId: this.project.id,
        projectName: this.project.name
      }
    },

    /**
     */
    closeConfirmProjectDeleteModal () {
      this.showConfirmProjectDelete = false
      this.projectToDelete = {}
    },

    /**
     */
    async confirmProjectDelete () {
      await this.deleteProject(this.projectToDelete.projectId)

      this.$router.push({ path: '/projects/' })
      this.closeConfirmProjectDeleteModal()
    },

    /**
     */
    async updateProjectVisibleToSales () {
      this.isProjectVisibleToSales = !this.isProjectVisibleToSales

      await this.updateProject({
        projectId: this.projectId,
        visibleToSales: this.isProjectVisibleToSales
      })
    },

    /**
     * @param   {number} boardLayout
     *
     * @returns {string}
     */
    imageResolution (boardLayout) {
      if (boardLayout <= 1) {
        return imageResolutions.STYLE
      } else {
        return imageResolutions.STYLE_THUMBNAIL
      }
    },

    /**
     */
    showPresent () {
      if (localStorage.getItem(`activeBoard-${this.projectId}`) !== undefined) {
        const item = `activeBoard-${this.projectId}`
        Utils.clearLocalStorage(item)
      }

      this.$router.push(`/projects/${this.projectId}/present`)
    },

    /**
     * @param   {string} itemTypeToExport -- collection|present|boards
     * @param   {number} quality
     *
     * @returns {string}
     */
    createPDFExportURL (itemTypeToExport, quality) {
      const baseURL = this.$router.options.base

      switch (itemTypeToExport) {
        case this.validPDFExportTypes.BOARDS:
          return `${baseURL}projects/${this.projectId}/export/boards?quality=${quality}`
        case this.validPDFExportTypes.COLLECTION:
          return `${baseURL}export/collection?group_id=${this.selectedGroupId}&season_id=${this.selectedSeasonId}`
        case this.validPDFExportTypes.PRESENT:
          return `${baseURL}projects/${this.projectId}/export/styles`
      }
    },

    /**
     * @param {object} payload
     * @param {string} payload.itemTypeToExport                 -- this.validPDFExportTypes: collection|present|boards
     * @param {number} [payload.quality=this.defaultPdfQuality]
     */
    printPDFCommand ({ itemTypeToExport, quality = this.defaultPdfQuality }) {
      if (
        itemTypeToExport &&
        this.validPDFExportTypes[itemTypeToExport.toUpperCase()]
      ) {
        window.open(this.createPDFExportURL(itemTypeToExport, quality), '_blank')
      }
    }
  }
}
</script>

<style lang="scss" scoped>
$min-width-board-label: 30px;
$width-board: 360px;
$height-board: 300px;
$height-board-list-mask-border: spacing(12);

.project {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.project__boards {
  @include size(100%, calc(100vh - #{$height-board-list-mask-border * 2}));

  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  overflow: auto;
}

.project__board {
  @include size($width-board, $height-board);

  position: relative;
  margin: $gutter;
  padding: $gutter;
  text-align: center;
  background-color: $white;
  border-radius: $border-radius-default;
  box-shadow: $shadow-black-opacity-15;
  cursor: pointer;

  &.project__board--click-disabled {
    cursor: default;
    pointer-events: none;
  }
}

.project__left {
  @include scroll-y-smooth;

  height: 100%;
}

.project__left-actions {
  display: flex;
  flex-direction: column;
  height: 100%;
  text-align: left;
}

.project__left-action {
  position: relative;
  display: flex;
  justify-content: space-between;
  padding: $spacer-unit 0;
  font-size: $font-size-large;
}

.project__delete-name {
  color: $grey-blue;
  text-transform: uppercase;
}
</style>
